import { forwardRef, useEffect, useRef, useState } from 'react'
import { isPresent, noop } from 'utils'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import useFocusOnError from 'utils/hooks/useFocusOnError'

let idIndex = 0

const InputWrapperPropTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  error: PropTypes.any,
  focusOnError: PropTypes.bool,
  maxLength: PropTypes.number,
  hideLength: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onError: PropTypes.func,
  onValueChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  InputComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  search: PropTypes.bool,
  onClickSearch: PropTypes.func,
  trim: PropTypes.bool,
  variableHeight: PropTypes.bool,
}

export const InputProptypes = {
  ...InputWrapperPropTypes,
  id: PropTypes.any,
  name: PropTypes.any,
  value: PropTypes.any,
  defaultValue: PropTypes.any,
  onError: PropTypes.func,
  onChange: PropTypes.func,
  maxLength: PropTypes.any,
}

const InputWrapper = forwardRef(
  (
    {
      id,
      value,
      defaultValue,
      name = '',
      placeholder = '',
      disabled = false,
      error = null,
      focusOnError = false,
      onChange = noop,
      onError = noop,
      onFocus = noop,
      onBlur = noop,
      onValueChange = noop,
      onKeyDown = noop,
      maxLength,
      hideLength = false,
      className = 'box_inptxt',
      InputComponent,
      trim = false,
      variableHeight = false,
      ...props
    },
    outerRef
  ) => {
    const [inputValue, setInputValue] = useState(value || defaultValue || '')
    const [uniqIndex] = useState(idIndex++)
    const [isFocused, setIsFocused] = useState(false)
    const innerRef = useRef()
    const ref = outerRef || innerRef
    const currentElementValue = ref?.current?.value

    useEffect(() => {
      setInputValue(_inputValue => {
        if (value !== undefined) return value || ''
        if (defaultValue !== undefined) return defaultValue || ''
        return _inputValue
      })
    }, [value, defaultValue, currentElementValue])

    useFocusOnError(ref, error, focusOnError)

    const contentLength = isPresent(inputValue)
      ? inputValue.toString().length || 0
      : 0

    const handleFocus = event => {
      setIsFocused(true)
      onFocus(event)
    }

    const handleBlur = event => {
      setIsFocused(false)
      trim && handleTrim(event)
      onBlur(event)
    }

    const handleInputChange = event => {
      setInputValue(event.target.value)
      onChange(event)
      onValueChange(event.target.value)
    }

    const handleTrim = event => {
      if (event.target.value) {
        event.target.value = event.target.value.trim()
      }
      handleInputChange(event)
    }

    useEffect(() => {
      if (disabled && isFocused && ref.current) ref.current.blur()
    }, [disabled, isFocused, ref])

    return (
      <>
        <span
          className={classNames(className, {
            on: isFocused,
            on_inp: !isFocused && isPresent(inputValue),
            in_active: disabled,
            in_error: error,
          })}
          style={
            variableHeight
              ? {
                  height: '100%',
                  minHeight: '36px',
                }
              : null
          }>
          {maxLength && !hideLength && (
            <span className="num_byte">
              <span className="screen_out">작성가능한 총 텍스트 수</span>
              {maxLength - contentLength > 0 ? maxLength - contentLength : 0}
            </span>
          )}

          <span className="inner_inp">
            {placeholder &&
              !isFocused &&
              (isPresent(inputValue) || (
                <label
                  htmlFor={id || `inpTxt_$$${uniqIndex}`}
                  className="lab_txt"
                  onClick={() => {
                    if (ref?.current?.contentEditable === 'true')
                      ref.current.focus()
                  }}>
                  {placeholder}
                </label>
              ))}
            <InputComponent
              id={id || `inpTxt_$$${uniqIndex}`}
              ref={ref}
              name={name}
              value={value === null ? '' : value}
              defaultValue={defaultValue}
              maxLength={maxLength}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={handleInputChange}
              onError={onError}
              disabled={disabled}
              onKeyDown={onKeyDown}
              {...props}
            />
          </span>
        </span>
      </>
    )
  }
)

InputWrapper.displayName = 'InputWrapper'
InputWrapper.propTypes = InputWrapperPropTypes

export const wrap = (Component, extraProps) => {
  const WrappedInput = forwardRef((props, ref) => (
    <InputWrapper
      {...{ ...props, ...extraProps }}
      InputComponent={Component}
      ref={ref}
    />
  ))
  WrappedInput.displayName = Component.name || Component.displayName
  WrappedInput.propTypes = InputWrapperPropTypes
  return WrappedInput
}
